import "./style.scss";
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import {
  setColumnWidth,
  setRowFieldValue,
  toggleAllRows,
  toggleRowSelection,
} from "@/src/store/slices/OfficeAccountingPage/officeAccountingTableSlice";
import { applicantsRequests } from "@/src/api/applicantsRequests";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { IApplicantAccountingUpdatePayload } from "@/src/types/officeAccounting";
import { IOfficeAccountingTableRow } from "@/src/types/officeAccounting";
import { IApplicantUpdatePayload } from "@/src/types/applicant";
import { OfficeAccountingColumnConfig } from "@/src/constants/officeAccountingTableConfig";
import { onlineApplicationsStarsConst } from "@/src/constants/applicationOnlineConsts";
import { getOfficeAccountingRowId } from "@/src/utils/officeAccountingMappers";
import { formatDateToDisplay, formatTodayToApiIso, formatTodayToDisplay } from "@/src/utils/formatDateToApiIso";
import {
  formatOfficeAccountingUpdateValue,
  mapOfficeAccountingColumnUpdate,
  OFFICE_ACCOUNTING_APPLICANT_CONFIRM_COLUMNS,
  OFFICE_ACCOUNTING_DATE_DISPLAY_COLUMNS,
  resolveInvitationFeeFromFormOfEducation,
} from "@/src/utils/officeAccountingColumnUpdate";
import ApplicationStarIcon from "@/src/components/ApplicationsPage/ApplicationStarIcon";
import VerticalLine from "@/src/components/VerticalLine/VerticalLine";
import ArrowFilter from "@/src/svg/applicationsPage/ArrowFilter";
import CellContent from "../CellContent/CellContent";
import OAPFilterPopUp from "../OAPFilterPopUp/OAPFilterPopUp";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { OpenOfficeAccountingFullNameParams } from "../openOfficeAccountingFullNamePopUp";

interface OAPBodyProps {
  loadActualForm: () => void;
  onOpenFullNamePopUp: (params: OpenOfficeAccountingFullNameParams) => void;
  isShowSettingsModal: boolean;
}

const CHECKER_WIDTH = 4.12;

const getCellValue = (row: IOfficeAccountingTableRow, columnId: string): string => {
  const value = row[columnId as keyof IOfficeAccountingTableRow];
  const displayValue = value == null ? "" : String(value);
  if (columnId === "invitationFee") {
    return displayValue || resolveInvitationFeeFromFormOfEducation(row.formOfEducation) || "";
  }
  if (OFFICE_ACCOUNTING_DATE_DISPLAY_COLUMNS.has(columnId)) {
    return formatDateToDisplay(displayValue) || displayValue;
  }
  return displayValue;
};

type RowChangeHandler = (row: IOfficeAccountingTableRow, columnId: string, value: string) => void;

interface TableRowProps {
  row: IOfficeAccountingTableRow;
  visibleColumns: OfficeAccountingColumnConfig[];
  selectedRows: number[];
  openTableParamMenu: string;
  openTableParamMenuRowId: number | null;
  onCellChange: RowChangeHandler;
  onToggleRowSelection: (rowId: number) => void;
  onToggleStarMenu: (rowId: number) => void;
  onStarChange: (rowId: number, color: string) => void;
  onOpenFullNamePopUp: (params: OpenOfficeAccountingFullNameParams) => void;
  loadActualForm: () => void;
}

const TableRow = memo(
  ({
    row,
    visibleColumns,
    selectedRows,
    openTableParamMenu,
    openTableParamMenuRowId,
    onCellChange,
    onToggleRowSelection,
    onToggleStarMenu,
    onStarChange,
    onOpenFullNamePopUp,
    loadActualForm,
  }: TableRowProps) => {
    const rowId = row.student_info_id;
    const displayId = getCellValue(row, "officeAccountingId");

    return (
      <tr className={selectedRows.includes(rowId) ? "table-row-selected" : undefined}>
        <td id="table-checker" style={{ width: `${CHECKER_WIDTH}rem`, minWidth: `${CHECKER_WIDTH}rem` }}>
          <div className="cell-wrapper">
            <label className="custom-checkbox">
              <input
                type="checkbox"
                checked={selectedRows.includes(rowId)}
                onChange={() => onToggleRowSelection(rowId)}
              />
              <span className="checkmark" />
            </label>
            <div
              className={`star-icon ${row.color_star}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleStarMenu(rowId);
              }}
            >
              <ApplicationStarIcon color={row.color_star} />
            </div>
            {openTableParamMenu === "star" && openTableParamMenuRowId === rowId && (
              <div className="star-menu">
                {onlineApplicationsStarsConst.map(({ color, description }) => (
                  <div
                    key={color}
                    className={`star-option ${color}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStarChange(rowId, color);
                    }}
                  >
                    <ApplicationStarIcon color={color} />
                    {description && description !== "" && <div className="tooltip">{description}</div>}
                  </div>
                ))}
              </div>
            )}
            <div className="vertical-line">
              <VerticalLine />
            </div>
          </div>
        </td>
        {visibleColumns.map((column) => {
          const cellValue = getCellValue(row, column.id);
          const columnWidth = column.width ?? 5;
          return (
            <td
              key={column.id}
              className={column.id}
              style={{ width: `${columnWidth}rem`, minWidth: `${columnWidth}rem` }}
            >
              <div className="cell-wrapper">
                <CellContent
                  column={{ id: column.id, value: cellValue }}
                  rowId={displayId}
                  studentInfoId={row.student_info_id}
                  applicantId={row.applicant_id}
                  commentsList={row.commentsList}
                  invitationFeePaidRecords={row.invitationFeePaidRecords}
                  paymentFactUsdRecords={row.paymentFactUsdRecords}
                  invoiceNumber={Array.isArray(row.invoiceNumber) ? row.invoiceNumber.join(", ") : row.invoiceNumber}
                  onRefreshComments={loadActualForm}
                  onOpenFullNamePopUp={onOpenFullNamePopUp}
                  onChange={(newValue) => onCellChange(row, column.id, newValue)}
                />
                <div className="vertical-line">
                  <VerticalLine />
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    );
  },
);

TableRow.displayName = "OAPBodyRow";

const OAPBody: FC<OAPBodyProps> = ({ loadActualForm, onOpenFullNamePopUp, isShowSettingsModal }) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const { visibleColumns, data, selectedRows } = useSelector((state: RootState) => state.officeAccountingTable);
  const tableFiltersList = useSelector((state: RootState) => state.officeAccountingFilters.filterParams ?? undefined);
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const allChecked = selectedRows.length === safeData.length && safeData.length > 0;
  const someChecked = selectedRows.length > 0 && selectedRows.length < safeData.length;
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const [filterPopUp, setFilterPopUp] = useState<string | null>(null);
  const [openTableParamMenu, setOpenTableParamMenu] = useState("");
  const [openTableParamMenuRowId, setOpenTableParamMenuRowId] = useState<number | null>(null);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someChecked && !allChecked;
    }
  }, [allChecked, someChecked]);

  const dragData = useRef<{ startX: number; thId: string | null; startWidth: number }>({
    startX: 0,
    thId: null,
    startWidth: 0,
  });

  const onColumnResizeMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isShowSettingsModal || !dragData.current.thId) return;
      const deltaRem = (e.clientX - dragData.current.startX) / 16;
      let newWidth = dragData.current.startWidth + deltaRem;
      if (newWidth < 2) newWidth = 2;
      dispatch(setColumnWidth({ id: dragData.current.thId, width: newWidth }));
    },
    [dispatch, isShowSettingsModal],
  );

  const onColumnResizeMouseUp = useCallback(() => {
    window.removeEventListener("mousemove", onColumnResizeMouseMove);
    window.removeEventListener("mouseup", onColumnResizeMouseUp);
    dragData.current = { startX: 0, thId: null, startWidth: 0 };
  }, [onColumnResizeMouseMove]);

  const onColumnResizeMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, thId: string, startWidth: number) => {
      if (!isShowSettingsModal) return;
      e.stopPropagation();
      e.preventDefault();
      dragData.current = { startX: e.clientX, thId, startWidth };
      window.addEventListener("mousemove", onColumnResizeMouseMove);
      window.addEventListener("mouseup", onColumnResizeMouseUp);
    },
    [isShowSettingsModal, onColumnResizeMouseMove, onColumnResizeMouseUp],
  );

  useEffect(
    () => () => {
      window.removeEventListener("mousemove", onColumnResizeMouseMove);
      window.removeEventListener("mouseup", onColumnResizeMouseUp);
    },
    [onColumnResizeMouseMove, onColumnResizeMouseUp],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);

  const isScrollTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      !target.closest("input") &&
      !target.closest(".custom-checkbox") &&
      !target.closest(".star-icon") &&
      !target.closest(".star-menu") &&
      !target.closest(".cell-wrapper-arrow") &&
      !target.closest(".office-accounting-page-filter") &&
      !target.closest(".vertical-line")
    );
  };

  const onScrollMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollRef.current || !isScrollTarget(e.target)) return;
    isDragging.current = true;
    startX.current = e.clientX;
    startScrollLeft.current = scrollRef.current.scrollLeft;
  }, []);

  const onScrollMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    scrollRef.current.scrollLeft = startScrollLeft.current - (e.clientX - startX.current);
  }, []);

  const stopScrollDrag = useCallback(() => {
    isDragging.current = false;
  }, []);

  const onScrollTouchStart = useCallback((e: React.TouchEvent) => {
    if (!scrollRef.current || !isScrollTarget(e.target)) return;
    isDragging.current = true;
    startX.current = e.touches[0].clientX;
    startScrollLeft.current = scrollRef.current.scrollLeft;
  }, []);

  const onScrollTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    scrollRef.current.scrollLeft = startScrollLeft.current - (e.touches[0].clientX - startX.current);
  }, []);

  const handleCellChange = useCallback<RowChangeHandler>(
    async (row, columnId, newValue) => {
      const isDateColumn = OFFICE_ACCOUNTING_DATE_DISPLAY_COLUMNS.has(columnId);
      const nextDisplayValue = isDateColumn ? formatDateToDisplay(newValue) || newValue : newValue;
      if (getCellValue(row, columnId) === nextDisplayValue) return;
      if (
        OFFICE_ACCOUNTING_APPLICANT_CONFIRM_COLUMNS.has(columnId) &&
        !window.confirm("Are you sure you want to change the table data?")
      ) {
        return;
      }

      const studentInfoId = getOfficeAccountingRowId(row);
      const invitationFeeIfDateIso =
        columnId === "invitationFee" && newValue.trim() ? formatTodayToApiIso() : "";
      const invitationFeeIfDateDisplay = invitationFeeIfDateIso ? formatTodayToDisplay() : "";

      dispatch(
        setRowFieldValue({
          studentInfoId,
          field: columnId as keyof IOfficeAccountingTableRow,
          value: nextDisplayValue,
        }),
      );
      if (columnId === "invitationFee" && invitationFeeIfDateDisplay) {
        dispatch(
          setRowFieldValue({
            studentInfoId,
            field: "ifDate",
            value: invitationFeeIfDateDisplay,
          }),
        );
      }

      const mapping = mapOfficeAccountingColumnUpdate(columnId);
      if (!mapping) return;

      const apiValue = formatOfficeAccountingUpdateValue(mapping, newValue);

      try {
        if (mapping.target === "accounting") {
          const payload = { [mapping.apiField]: apiValue } as IApplicantAccountingUpdatePayload;
          if (invitationFeeIfDateIso) {
            payload.if_date = invitationFeeIfDateIso;
          }
          const result = await officeAccountingPageRequests.updateApplicantAccounting(studentInfoId, payload);
          if (result == null) throw new Error("Accounting update failed");
          return;
        }

        const applicantId = row.applicant_id;
        if (!applicantId) throw new Error("Applicant ID is missing");

        const payload = { [mapping.apiField]: apiValue } as IApplicantUpdatePayload;
        const result = await applicantsRequests.update(applicantId, payload);
        if (result == null) throw new Error("Applicant update failed");
      } catch (error) {
        console.error("Office Accounting update error:", error);
        notify({ title: "Update failed", message: "Could not save changes.", type: "error" });
        loadActualForm();
      }
    },
    [dispatch, loadActualForm, notify],
  );

  const handleStarChange = useCallback(
    async (rowId: number, color: string) => {
      setOpenTableParamMenu("");
      setOpenTableParamMenuRowId(null);

      const targetRow = safeData.find((row) => row.student_info_id === rowId);
      if (!targetRow || targetRow.color_star === color) return;

      dispatch(
        setRowFieldValue({
          studentInfoId: rowId,
          field: "color_star",
          value: color,
        }),
      );

      try {
        const result = await officeAccountingPageRequests.updateApplicantAccounting(rowId, { color_star: color });
        if (result == null) throw new Error("Star update failed");
        notify({
          title: "Star updated",
          message: "Record marker updated.",
          type: "success",
        });
      } catch (error) {
        console.error("Office Accounting star update error:", error);
        notify({ title: "Update failed", message: "Could not update star marker.", type: "error" });
        loadActualForm();
      }
    },
    [dispatch, loadActualForm, notify, safeData],
  );

  const handleToggleStarMenu = useCallback(
    (rowId: number) => {
      if (openTableParamMenu === "star" && openTableParamMenuRowId === rowId) {
        setOpenTableParamMenu("");
        setOpenTableParamMenuRowId(null);
        return;
      }
      setOpenTableParamMenu("star");
      setOpenTableParamMenuRowId(rowId);
    },
    [openTableParamMenu, openTableParamMenuRowId],
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".star-menu") && !target.closest(".star-icon")) {
        setOpenTableParamMenu("");
        setOpenTableParamMenuRowId(null);
      }
      if (!target.closest(".office-accounting-page-filter") && !target.closest(".cell-wrapper-arrow")) {
        setFilterPopUp(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div
      ref={scrollRef}
      className="office-accounting-table"
      onMouseMove={onScrollMouseMove}
      onMouseUp={stopScrollDrag}
      onMouseLeave={stopScrollDrag}
      onTouchMove={onScrollTouchMove}
      onTouchEnd={stopScrollDrag}
      onTouchCancel={stopScrollDrag}
    >
      <table>
        <thead onMouseDown={onScrollMouseDown} onTouchStart={onScrollTouchStart}>
          <tr>
            <th id="table-checker" style={{ width: `${CHECKER_WIDTH}rem`, minWidth: `${CHECKER_WIDTH}rem` }}>
              <div className="cell-wrapper">
                <label className={`custom-checkbox ${someChecked && !allChecked ? "indeterminate" : ""}`}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={headerCheckboxRef}
                    onChange={() => dispatch(toggleAllRows())}
                  />
                  <span className="checkmark" />
                </label>
                <div
                  className={`cell-wrapper-arrow ${filterPopUp === "color_star" ? "rotate" : ""}`}
                  onClick={() => setFilterPopUp(filterPopUp === "color_star" ? null : "color_star")}
                >
                  <ArrowFilter />
                </div>
                <div className="vertical-line">
                  <VerticalLine />
                </div>
                <OAPFilterPopUp
                  id="color_star"
                  tableFiltersList={tableFiltersList}
                  filterPopUp={filterPopUp}
                  setFilterPopUp={setFilterPopUp}
                />
              </div>
            </th>
            {visibleColumns.map((column) => {
              const columnWidth = column.width ?? 5;
              const isCampusFilter = column.id === "campus";
              return (
                <th
                  key={column.id}
                  className={column.id}
                  style={{ width: `${columnWidth}rem`, minWidth: `${columnWidth}rem` }}
                >
                  <div className="cell-wrapper">
                    <p>{column.title}</p>
                    {isCampusFilter && (
                      <div
                        className={`cell-wrapper-arrow ${filterPopUp === "campus" ? "rotate" : ""}`}
                        onClick={() => setFilterPopUp(filterPopUp === "campus" ? null : "campus")}
                      >
                        <ArrowFilter />
                      </div>
                    )}
                    <div
                      className="vertical-line"
                      onMouseDown={(e) => onColumnResizeMouseDown(e, column.id, columnWidth)}
                      style={{ cursor: isShowSettingsModal ? "ew-resize" : undefined }}
                    >
                      <VerticalLine />
                    </div>
                    {isCampusFilter && (
                      <OAPFilterPopUp
                        id="campus"
                        tableFiltersList={tableFiltersList}
                        filterPopUp={filterPopUp}
                        setFilterPopUp={setFilterPopUp}
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {safeData.length === 0 ? (
            <tr>
              <td colSpan={visibleColumns.length + 1}>
                <div className="office-accounting-table-empty">
                  <p>No records found.</p>
                </div>
              </td>
            </tr>
          ) : (
            safeData.map((row) => (
              <TableRow
                key={row.student_info_id}
                row={row}
                visibleColumns={visibleColumns}
                selectedRows={selectedRows}
                openTableParamMenu={openTableParamMenu}
                openTableParamMenuRowId={openTableParamMenuRowId}
                onCellChange={handleCellChange}
                onToggleRowSelection={(rowId) => dispatch(toggleRowSelection(rowId))}
                onToggleStarMenu={handleToggleStarMenu}
                onStarChange={handleStarChange}
                onOpenFullNamePopUp={onOpenFullNamePopUp}
                loadActualForm={loadActualForm}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default OAPBody;
